import { invariant } from '@spotify/confidence-core-react';
import {
  StatsDataFragment,
  StatsHypothesisResultFragment,
  StatsV2AnalysisStatsAnnotationContext,
} from '@spotify/confidence-graphql';
import { useWorkflowInstanceContext } from '@spotify/plugin-confidence-workflows-react';
import { useIsStatsModuleStrategySequentialV2 } from '@spotify/plugin-confidence-workflows-react';

import { convertAnalysisResults } from '../helpers';
import { AnnotationV2 } from '../types';
import { StatsData2 } from '../types';

export const getMetricAnnotations = (
  results?: StatsHypothesisResultFragment[],
): Record<string, AnnotationV2[]> => {
  if (!results) {
    return {};
  }

  const annotationMap: Record<string, AnnotationV2[]> = {};
  ((results ?? []).flatMap(result => result?.annotations) ?? []).forEach(
    annotation => {
      const context = (annotation?.context ?? []).find(
        item =>
          item.key ===
          StatsV2AnalysisStatsAnnotationContext.StatsAnnotationContextHypothesis,
      );
      if (context === undefined) {
        return;
      }

      if (context.value) {
        if (!Object.hasOwn(annotationMap, context.value)) {
          annotationMap[context.value] = [];
        }
        annotationMap[context.value].push({
          info: annotation.info ?? undefined,
          warning: annotation.warning ?? undefined,
          error: annotation.error ?? undefined,
          context: annotation.context ?? undefined,
        });
      }
    },
  );
  return annotationMap;
};

export const useStatsData = (): StatsData2 => {
  const { isLive, moduleData2 } = useWorkflowInstanceContext();

  const statsData: StatsDataFragment | undefined | null = moduleData2?.stats;
  invariant(statsData, 'useStatsData: stats data is required');

  const isSequential = useIsStatsModuleStrategySequentialV2(statsData);
  const isFixedHorizon = !isSequential;

  return {
    analysisResults: convertAnalysisResults(statsData.analysisResults),
    isSequential,
    isFixedHorizon,
    canHaveResults: !isFixedHorizon || !isLive,
  };
};
